import { Box, Flex, Skeleton } from '@chakra-ui/react';
import { Checkbox, FormControlLabel } from '@material-ui/core';
import RadioButtonCheckedIcon from '@material-ui/icons/RadioButtonChecked';
import RadioButtonUncheckedIcon from '@material-ui/icons/RadioButtonUnchecked';
import {
  Dropdown,
  DropdownChip,
  DropdownContent,
  DropdownRadioGroup,
  DropdownRadioItem,
  DropdownTrigger,
} from '@mesh.ai/rabbit';
import { StyledPaper, StyledTypography } from 'styles/common';

import { PaperWithShadow } from 'uiToolkitV1/Paper';
import { TextBodyMedium, TextHeader3 } from 'uiToolkitV1/Typography';

const AUTO_APPROVE_DROPDOWN_OPTIONS_TREND = ['0', '1', '2', '3', '4', '5', '6', '7', '10', '14'];

const ApprovalRequiredCheckBoxSection = ({ isApprovalRequired, setIsApprovalRequired }) => {
  const checkboxOptions = [
    {
      name: 'approvalRequired',
      checked: isApprovalRequired,
      onChange: () => setIsApprovalRequired(true),
      label: 'Approval required',
    },
    {
      name: 'approvalNotRequired',
      checked: !isApprovalRequired,
      onChange: () => setIsApprovalRequired(false),
      label: 'No approval required',
    },
  ];
  return (
    <Flex py="12px" flexDirection="row" gap="12px">
      {checkboxOptions.map((option) => {
        const { onChange, checked, name, label } = option;
        return (
          <FormControlLabel
            control={
              <Checkbox
                checked={checked}
                onChange={onChange}
                name={name}
                color="#0D819F"
                icon={<RadioButtonUncheckedIcon fontSize="small" />}
                checkedIcon={<RadioButtonCheckedIcon style={{ color: '#0D819F' }} fontSize="small" />}
              />
            }
            label={<TextBodyMedium>{label}</TextBodyMedium>}
          />
        );
      })}
    </Flex>
  );
};

const AutoApprovalDropdown = ({ selected, setSelected }) => (
  <Dropdown>
    <Flex flexDirection="row" alignItems="center">
      <TextBodyMedium width="155px">Automatically approved </TextBodyMedium>
      <Box
        sx={{
          'button[data-state="open"]': {
            borderBottom: '1px solid #0D819F',
          },
          'button[data-state="closed"]': {
            borderBottom: '1px dashed #999999',
          },
          'button[data-state="open"] .icon': {
            color: '#0D819F',
            transform: 'rotate(180deg)',
            transition: 'transform 0.2s',
          },
          "button[data-state='closed'] .icon": {
            color: '#333333',
          },
        }}
        paddingLeft="8px"
        paddingRight="8px"
        minWidth="210px"
      >
        <DropdownTrigger>
          <DropdownChip>
            <TextBodyMedium minWidth="210px" textAlign="left">
              {!selected ? 'Never' : `After ${selected} ${Number(selected) > 1 ? 'days' : 'day'}`}
            </TextBodyMedium>
          </DropdownChip>
        </DropdownTrigger>
      </Box>
    </Flex>
    <DropdownContent align="start" style={{ minWidth: '240px' }}>
      <DropdownRadioGroup value={selected}>
        {AUTO_APPROVE_DROPDOWN_OPTIONS_TREND.map((day) => (
          <DropdownRadioItem
            value={day}
            onClick={() => {
              setSelected(day);
            }}
          >
            {day === '0' ? 'Never' : `After ${day} ${Number(day) > 1 ? 'days' : 'day'}`}
          </DropdownRadioItem>
        ))}
      </DropdownRadioGroup>
    </DropdownContent>
  </Dropdown>
);

const ApprovalSection = ({
  isApprovalRequired,
  setIsApprovalRequired,
  autoApproveDays,
  setAutoApproveDays,
  isLoading,
  isError,
}) => {
  if (isLoading) {
    return (
      <Box display="flex" mb={3}>
        <Skeleton height="100px" width="100%" borderRadius="8px" startColor="surface.F5F5F5" endColor="surface.g3" />
      </Box>
    );
  }

  if (isError) {
    return (
      <StyledPaper padding="1rem" elevation={0} showBorder bottomGutter={1}>
        <StyledTypography variant="body2" color="red" align="center">
          Error occurred while fetching disciplinary violations
        </StyledTypography>
      </StyledPaper>
    );
  }
  return (
    <PaperWithShadow p={3} mb={3}>
      <Box borderBottom="1px solid #E7E7E7" py="4px">
        <TextHeader3 bold>Admin approval workflow</TextHeader3>
      </Box>
      <ApprovalRequiredCheckBoxSection
        isApprovalRequired={isApprovalRequired}
        setIsApprovalRequired={setIsApprovalRequired}
      />
      <AutoApprovalDropdown selected={autoApproveDays} setSelected={setAutoApproveDays} />
    </PaperWithShadow>
  );
};

export default ApprovalSection;
